<script setup lang="ts">
import type { Canvass } from "@/canvasses/types";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import { useCreateContact, useNewContact } from "@/contacts/contactsCrud";
import type { Contact } from "@/contacts/types";
import { useUpdateConversation } from "@/conversations/conversationsCrud";
import { useGetHouse } from "@/houses/housesCrud";
import { useNotifications } from "@/notifications/useNotifications";
import { useRoute, useRouter } from "vue-router";
import ConversationGuideAccordion from "./ConversationGuideAccordion.vue";
import NewContactForm from "./NewContactForm.vue";
import ViewHeader from "./ViewHeader.vue";

const { canvass, houseId } = defineProps<{
  canvass: Canvass;
  houseId: number;
}>();

const router = useRouter();
const route = useRoute();
const linkToConversation = route.query.linkToConversation as string | undefined;

const { house, getHouseStatus } = useGetHouse(houseId);
const { addPendingNotification } = useNotifications();
const { contact } = useNewContact(houseId);
const { updateConversation } = useUpdateConversation(houseId);
const { createContact, createContactStatus } = useCreateContact(houseId, {
  onSuccess: (createdContact: Contact) => {
    if (linkToConversation) {
      updateConversation({
        id: linkToConversation,
        contactId: createdContact.id,
      });
    }
    addPendingNotification("Der Kontakt wurde erfolgreich gespeichert.");
    router.replace({
      name: "canvassFlowEditContact",
      params: { contactId: createdContact.id },
    });
  },
});
</script>

<template>
  <HandleQueryStatus :query-status="getHouseStatus">
    <template v-if="house !== undefined">
      <ViewHeader
        :redirect-on-close-target="{
          name: 'canvassFlowHouseContacts',
        }"
      >
        Neuen Kontakt anlegen
        <template #subheading>
          Adresse: {{ house.street }} {{ house.houseNumber }}
        </template>
      </ViewHeader>
      <div class="box column container container-lg">
        <ConversationGuideAccordion
          v-if="canvass.conversationGuide"
          :conversation-guide="canvass.conversationGuide"
        />
      </div>
      <NewContactForm
        v-model="contact"
        :address="house"
        :saving-status="createContactStatus"
        @save="createContact(contact)"
      />
    </template>
  </HandleQueryStatus>
</template>
